import { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { ThemeContext } from 'styled-components'

import StandardButton from 'design-system/Buttons/StandardButton'
import { Grid } from 'design-system/Layouts/Grid'
import Body2 from 'design-system/Typography/Body/Body2'
import Heading2 from 'design-system/Typography/Heading/Heading2'
import Heading4 from 'design-system/Typography/Heading/Heading4'
import Title1 from 'design-system/Typography/Title/Title1'

import BackgroundImage from 'components/BackgroundImage'
import { CheckmarkThin } from 'components/SVG/Checkmark'
import Spacer from 'components/Spacer'
import ViewportMarginContainer from 'components/ViewportMarginContainer'

import { HowItWorksData } from 'data/HowItWorksData'

interface ISelectedProp {
  selected?: boolean
}

const StepProgressNumber = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StepProgressContentGrid = styled(Grid)`
  padding-bottom: 24px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding-bottom: 40px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding-bottom: 64px;
  }
`

const Heading4Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 40px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    height: 54px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    height: 62px;
  }
`

const ListContainer = styled.div`
  width: 100%;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    width: 400px;
  }
`

const List = styled.div<ISelectedProp>`
  display: flex;
  align-items: flex-start;
  svg {
    fill: ${props => (props.selected ? props.theme.colors.gold500 : props.theme.colors.neutral500)};
    flex-shrink: 0;
  }
`

const Circle = styled.div<ISelectedProp>`
  background-color: ${props =>
    props.selected ? props.theme.colors.blue500 : props.theme.colors.white};
  border: 3px solid
    ${props => (props.selected ? props.theme.colors.blue500 : props.theme.colors.neutral100)};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  .title1 {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: ${props => (props.selected ? props.theme.colors.white : props.theme.colors.neutral100)};
    padding-top: 2px;
  }

  transition: background-color 100ms ease-out;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: 54px;
    height: 54px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    width: 62px;
    height: 62px;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  margin-top: 20px;
  border-radius: ${props => props.theme.radii[24]}px;
  box-shadow: ${props => props.theme.shadows.neutral.level2};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: 540px;
    height: 240px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex: 1;
    width: 440px;
    height: 260px;
    margin: 0;
  }
`

const CheckmarkIcon = styled.div`
  padding-top: 5px;
`

interface IEdgeLineProp {
  edgeLine?: boolean
}

const ProgressLine = styled.div<IEdgeLineProp & ISelectedProp>`
  width: 5px;
  height: 100%;
  background-color: ${props =>
    props.selected ? props.theme.colors.blue500 : props.theme.colors.neutral100};
  transition: background-color 100ms ease-in;
`

const HowItWorks: FunctionComponent = () => {
  const theme = useContext(ThemeContext)

  const { ref: step1Ref, inView: step1InView } = useInView({ threshold: 1, rootMargin: '-20% 0px' })
  const { ref: step2Ref, inView: step2InView } = useInView({ threshold: 1, rootMargin: '-20% 0px' })
  const { ref: step3Ref, inView: step3InView } = useInView({ threshold: 1, rootMargin: '-20% 0px' })
  const { ref: step4Ref, inView: step4InView } = useInView({ threshold: 1, rootMargin: '-20% 0px' })

  const refs = [step1Ref, step2Ref, step3Ref, step4Ref]
  const inViews = [step1InView, step2InView, step3InView, step4InView]
  const [inViewIndex, setInViewIndex] = useState(-1)

  useEffect(() => {
    setInViewIndex(inViews.lastIndexOf(true))
  }, [inViews])

  return (
    <ViewportMarginContainer center>
      <Spacer
        size={theme.spacings[32]}
        tabletSize={theme.spacings[48]}
        desktopSize={theme.spacings[72]}
      />
      <Heading2 textAlign="center">How it Works</Heading2>
      <Spacer
        size={theme.spacings[36]}
        tabletSize={theme.spacings[40]}
        desktopSize={theme.spacings[56]}
      />
      <Grid
        gridColumnsMobile="48px 1fr"
        gridColumns="56px 1fr"
        columnGapMobile="24px"
        columnGap="48px"
        rowGap="0px"
      >
        {HowItWorksData.map((data, index) => (
          <>
            <StepProgressNumber>
              <Circle
                ref={refs[index]}
                selected={inViewIndex === index}
              >
                <Title1>{`0${index + 1}`}</Title1>
              </Circle>
              <ProgressLine selected={inViewIndex === index} />
            </StepProgressNumber>
            <StepProgressContentGrid
              gridColumns="1fr"
              gridColumnsDesktop="1fr 1fr"
            >
              <ListContainer>
                <Heading4Wrapper>
                  <Heading4 color={inViewIndex === index ? 'blue600' : 'neutral900'}>
                    {data.title}
                  </Heading4>
                </Heading4Wrapper>
                {data.details.map(list => (
                  <Fragment key={list}>
                    <Spacer size={16} />
                    <List selected={inViewIndex === index}>
                      <CheckmarkIcon>
                        <CheckmarkThin width={20} />
                      </CheckmarkIcon>
                      <Spacer
                        size={12}
                        desktopSize={24}
                      />
                      <Body2 color="neutral500">{list}</Body2>
                    </List>
                  </Fragment>
                ))}
              </ListContainer>
              <ImageContainer>
                <BackgroundImage
                  backgroundSize="cover"
                  imgSrc={data.imgUrl}
                />
              </ImageContainer>
            </StepProgressContentGrid>
          </>
        ))}
      </Grid>
      <Spacer
        size={theme.spacings[36]}
        tabletSize={theme.spacings[40]}
        desktopSize={theme.spacings[56]}
      />
      <StandardButton
        type="primary"
        size="medium"
        label="Get Started"
        href="/listing/create"
        boxShadow={theme.shadows.blue.level1}
        center
      />
      <Spacer
        size={theme.spacings[32]}
        tabletSize={theme.spacings[48]}
        desktopSize={theme.spacings[72]}
      />
    </ViewportMarginContainer>
  )
}

export default HowItWorks
